import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysSavedFilter } from '../queryKeys';

import { createSavedFilter } from './createSavedFilter';
import {
  type CreateSavedFilterMutationOptions,
  type CreateSavedFilterPayload,
} from './types';

/**
 * React Query based custom hook for creating saved filters.
 *
 * Invalidates the saved-filter list for the created filter type on settle.
 *
 * @param options - Optional mutation options.
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/create_saved_filter_user_saved_filters_post
 */
export const useMutationCreateSavedFilter = (
  options?: CreateSavedFilterMutationOptions,
) => {
  const queryClient = useQueryClient();
  const { onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (payload: CreateSavedFilterPayload) =>
      createSavedFilter(payload),
    onSettled(data, error, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysSavedFilter.getAll(variables.type),
      });

      onSettled?.(data, error, variables, onMutateResult, context);
    },
    ...mutationOptions,
  });
};
